import React, { useState } from "react";
import { motion } from "framer-motion";
import { Play, Shield, Zap, Terminal, Sparkles } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const EXAMPLES = [
  {
    label: "100 UUIDs",
    runtime: "python",
    code:
      "import uuid\nfor _ in range(5):\n    print(uuid.uuid4())\nprint('...95 more')",
  },
  {
    label: "Prime sieve",
    runtime: "python",
    code:
      "def primes(n):\n    sieve = [True]*(n+1)\n    sieve[0]=sieve[1]=False\n    for i in range(2,int(n**.5)+1):\n        if sieve[i]:\n            for j in range(i*i,n+1,i): sieve[j]=False\n    return [i for i,v in enumerate(sieve) if v]\nprint(primes(50))",
  },
  {
    label: "Node hello",
    runtime: "node",
    code:
      "const arr = Array.from({length:5}, (_, i) => i*i);\nconsole.log('squares:', arr);\nconsole.log('node version:', process.version);",
  },
  {
    label: "Bash env",
    runtime: "bash",
    code: 'echo "whoami: $(whoami)"\necho "date: $(date -u)"\nuname -a',
  },
];

export default function SandboxHero() {
  const [idx, setIdx] = useState(0);
  const [code, setCode] = useState(EXAMPLES[0].code);
  const [runtime, setRuntime] = useState(EXAMPLES[0].runtime);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);

  const pickExample = (i) => {
    setIdx(i);
    setCode(EXAMPLES[i].code);
    setRuntime(EXAMPLES[i].runtime);
    setResult(null);
  };

  const run = async () => {
    setRunning(true);
    setResult(null);
    try {
      const res = await fetch(`${API}/api/docker-sandbox/execute`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ runtime, code, timeout: 8 }),
      });
      setResult(await res.json());
    } catch (e) {
      setResult({ status: "error", stderr: String(e), exit_code: 1 });
    } finally {
      setRunning(false);
    }
  };

  return (
    <section
      className="relative py-24"
      data-testid="sandbox-hero"
    >
      <div className="max-w-7xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 mb-6 rounded-full bg-gradient-to-r from-emerald-500/10 to-cyan-500/10 border border-emerald-500/20">
            <Shield className="w-4 h-4 text-emerald-400" />
            <span className="text-sm text-emerald-300 font-medium">
              Live MicroVM Sandbox — no sign-up
            </span>
          </div>
          <h2 className="text-4xl md:text-6xl font-black mb-4">
            Try NEXUS <span className="bg-gradient-to-r from-emerald-400 to-cyan-400 bg-clip-text text-transparent">Sandbox</span>
          </h2>
          <p className="text-lg text-gray-400 max-w-2xl mx-auto">
            Agent-grade isolated code execution — Python, Node, Bash. Rate-limited,
            resource-capped, persisted. Inspired by Microsoft Copilot MicroVMs.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.15 }}
          className="rounded-3xl border border-zinc-800 bg-gradient-to-br from-zinc-950 to-black overflow-hidden shadow-2xl shadow-emerald-500/5"
        >
          {/* Tabs */}
          <div className="flex flex-wrap items-center gap-2 px-5 py-3 border-b border-zinc-800 bg-black/50">
            <Terminal className="w-4 h-4 text-emerald-400" />
            <span className="text-xs text-zinc-400 mr-2 tracking-wider uppercase">
              Examples:
            </span>
            {EXAMPLES.map((ex, i) => (
              <button
                key={ex.label}
                onClick={() => pickExample(i)}
                data-testid={`sandbox-example-${i}`}
                className={`px-3 py-1 rounded-full text-xs font-medium transition ${
                  idx === i
                    ? "bg-emerald-500 text-black"
                    : "bg-zinc-900 text-zinc-300 hover:bg-zinc-800"
                }`}
              >
                {ex.label}
              </button>
            ))}
            <span className="ml-auto text-xs text-zinc-500 font-mono">
              runtime: {runtime}
            </span>
          </div>

          {/* Grid: editor + output */}
          <div className="grid lg:grid-cols-2 divide-x divide-zinc-800">
            <div className="p-5">
              <textarea
                value={code}
                onChange={(e) => setCode(e.target.value)}
                rows={10}
                spellCheck={false}
                data-testid="sandbox-hero-code"
                className="w-full font-mono text-sm bg-black text-emerald-200 border border-zinc-800 rounded-xl p-4 focus:border-emerald-500/50 focus:outline-none leading-relaxed"
              />
              <button
                onClick={run}
                disabled={running}
                data-testid="sandbox-hero-run"
                className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-gradient-to-r from-emerald-500 to-cyan-500 text-black font-semibold hover:brightness-110 disabled:opacity-50 transition"
              >
                <Play className="w-4 h-4" />
                {running ? "Executing…" : "Run in sandbox"}
              </button>
            </div>
            <div className="p-5 bg-black/40">
              <div className="flex items-center gap-2 text-xs text-zinc-400 mb-3">
                <Zap className="w-3.5 h-3.5" />
                Output
                {result && (
                  <span
                    className={`ml-auto px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                      result.status === "ok"
                        ? "bg-emerald-500/20 text-emerald-300"
                        : "bg-rose-500/20 text-rose-300"
                    }`}
                  >
                    exit {result.exit_code} · {result.duration_ms ?? "—"}ms
                  </span>
                )}
              </div>
              <pre
                data-testid="sandbox-hero-output"
                className="font-mono text-xs text-zinc-200 min-h-[14rem] max-h-[18rem] overflow-auto whitespace-pre-wrap"
              >
                {result
                  ? result.stdout || result.stderr || "(no output)"
                  : "Click “Run in sandbox” to execute this snippet live.\nYour code runs in an isolated, resource-capped environment."}
              </pre>
              {result?.stderr && result.status === "ok" && (
                <pre className="mt-2 font-mono text-xs text-amber-300 whitespace-pre-wrap">
                  {result.stderr}
                </pre>
              )}
            </div>
          </div>

          {/* Feature strip */}
          <div className="grid sm:grid-cols-3 border-t border-zinc-800 text-xs text-zinc-400">
            <FeatureCell icon={Shield} label="Isolated" value="MicroVM / subprocess" />
            <FeatureCell icon={Zap} label="Rate-limited" value="20 runs / min / IP" />
            <FeatureCell icon={Sparkles} label="Persisted" value="Every run auditable" />
          </div>
        </motion.div>
      </div>
    </section>
  );
}

function FeatureCell({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center gap-3 px-5 py-3 border-r border-zinc-800 last:border-r-0">
      <Icon className="w-4 h-4 text-emerald-400" />
      <div>
        <div className="text-[10px] uppercase tracking-wider text-zinc-500">
          {label}
        </div>
        <div className="text-zinc-200">{value}</div>
      </div>
    </div>
  );
}
